import {
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { Link as RouterLink } from "react-router-dom"
import appIcons from "@utils/appIcons"
import GenericDialog from "@cmp/genericDialog"
import {
  ProgramClassClient,
  StudentCourseClass,
  StudentProgram,
} from "@clients/programClassClient"
import { ProgramClient } from "@clients/programClient"
import { CourseClassRegistrationClient } from "@clients/courseClassRegistrationClient"
import { selectLoggedInUser } from "@features/login/loginSelectors"
import { useAppSelector } from "@app/hooks"
import { TrackClient } from "@clients/trackClient"
import { ProgramDto } from "@masterschool/course-builder-api"
import { formatProgramLabel, formatProgramName } from "./programLabel"
import { AddProgramDialog } from "./addProgramDialog"
import { useAppDispatch } from "@app/hooks"
import { registerStudentToCourseUpdated } from "@features/program/programSlice"

// The full offering descriptor for the chip's tooltip / removal copy.
const programFullLabel = (program: StudentProgram) =>
  program.domain ? formatProgramLabel(program) : program.marketingDescription

const chipHighlightSx = (highlighted: boolean | undefined) =>
  highlighted
    ? {
        borderColor: "primary.main",
        backgroundColor: "primary.50",
        color: "primary.main",
      }
    : undefined

// The tile-body scroll affordance: thin scrollbar plus fade-at-the-cut
// shadows that only show while content continues past the edge (the local-
// attachment covers slide over them at either end). Cover color = the panel
// background the tiles sit on.
const tileScrollSx = {
  overflowY: "auto",
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    borderRadius: "3px",
  },
  background: `
    linear-gradient(#fafaf7 30%, rgba(250, 250, 247, 0)) center top,
    linear-gradient(rgba(250, 250, 247, 0), #fafaf7 70%) center bottom,
    radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0)) center top,
    radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0)) center bottom`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "100% 32px, 100% 32px, 100% 12px, 100% 12px",
  backgroundAttachment: "local, local, scroll, scroll",
} as const

// The "Manage programs" tile: the student's active programs and classes as
// wrapping chip clouds in ONE scrollable tile body (the full lists, inline —
// no popovers), with the type-to-filter input and the add action pinned below
// it. Program chips deep-link to their program page; the chip BODY is the
// link, the X still removes. Removal is reversible (re-add anytime) and never
// a dropout, so a plain confirm is enough — the armed-timer confirm is
// reserved for the destructive/revenue actions (e.g. dropout).
export function ManageProgramsStrip(props: {
  studentId: string
  studentName?: string
  showHeader?: boolean
}) {
  const { studentId, studentName, showHeader = true } = props
  const dispatch = useAppDispatch()
  // Registering to a course is a peer of adding a program — same student, same
  // row. The existing popup is program-agnostic (course > term > class) and only
  // uses the name for its title, so the display name is enough here.
  const openRegisterToCourse = () => {
    const [firstName = "", ...rest] = (studentName ?? "").trim().split(" ")
    dispatch(
      registerStudentToCourseUpdated({
        studentId,
        firstName,
        lastName: rest.join(" "),
      }),
    )
  }
  const [programs, setPrograms] = useState<StudentProgram[] | null>(null)
  const [unattributedClasses, setUnattributedClasses] = useState<
    StudentCourseClass[]
  >([])
  const [error, setError] = useState(false)
  const [pendingRemoval, setPendingRemoval] = useState<StudentProgram | null>(
    null,
  )
  const [isRemoving, setIsRemoving] = useState(false)
  const [pendingClassRemoval, setPendingClassRemoval] =
    useState<StudentCourseClass | null>(null)
  const actingUser = useAppSelector(selectLoggedInUser)
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [classFilter, setClassFilter] = useState("")
  const scrollBodyRef = useRef<HTMLDivElement | null>(null)

  // The cards carry the OFFERING client id (the membership remove acts on), but
  // the chip needs two more things the card lacks: the campus program id (the
  // program page routes on it) and the program's real NAME. The program list
  // (each program with its offering ids as classesIds, plus its syllabusId) is
  // the frontend bridge to both. Best-effort: while it loads (or if it fails)
  // the chip is an unlinked, school-labelled chip.
  const [programByOffering, setProgramByOffering] = useState<Map<
    string,
    ProgramDto
  > | null>(null)
  // The program's real name is its track (syllabus) name — the SAME source the
  // add-program picker labels by, so a chip reads "IHK Data Analytics", not the
  // school "Data Analysis". syllabusId -> track name.
  const [trackNameBySyllabusId, setTrackNameBySyllabusId] = useState<
    Map<string, string>
  >(new Map())
  useEffect(() => {
    let cancelled = false
    ProgramClient.listPrograms()
      .then((allPrograms) => {
        if (cancelled) {
          return
        }
        const map = new Map<string, ProgramDto>()
        for (const program of allPrograms) {
          for (const offeringClientId of program.classesIds) {
            map.set(offeringClientId, program)
          }
        }
        setProgramByOffering(map)
      })
      .catch(() => {})
    TrackClient.list()
      .then((tracks) => {
        if (cancelled) {
          return
        }
        const names = new Map<string, string>()
        for (const track of tracks) {
          const name = track.externalName || track.internalName
          if (name) {
            names.set(track.id, name)
          }
        }
        setTrackNameBySyllabusId(names)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  // The program's name · cohort label, resolved exactly like the add-program
  // picker: offering -> program -> syllabus/track name. Falls back to school +
  // cohort while the maps load, and to the marketing description when the
  // offering has no program behind it.
  const programLabel = useCallback(
    (program: StudentProgram): string => {
      if (!program.domain) {
        return program.marketingDescription
      }
      const syllabusId = programByOffering?.get(
        program.offeringClientId,
      )?.syllabusId
      const name = syllabusId
        ? trackNameBySyllabusId.get(syllabusId)
        : undefined
      return formatProgramName({
        name,
        domain: program.domain,
        startDate: program.startDate,
      })
    },
    [programByOffering, trackNameBySyllabusId],
  )

  // Campus dynos can cold-start slowly and time out the first call; the retry
  // then hits a warm dyno, so try once more before surfacing an error.
  const load = useCallback(() => {
    setError(false)
    setPrograms(null)
    const attempt = (retriesLeft: number) => {
      ProgramClassClient.getStudentPrograms(studentId)
        .then((response) => {
          setPrograms(response.cards)
          setUnattributedClasses(response.unattributedClasses)
        })
        .catch(() => {
          if (retriesLeft > 0) {
            window.setTimeout(() => attempt(retriesLeft - 1), 1500)
          } else {
            setError(true)
          }
        })
    }
    attempt(1)
  }, [studentId])

  useEffect(() => {
    load()
  }, [load])

  const confirmRemove = () => {
    if (!pendingRemoval) {
      return
    }
    setIsRemoving(true)
    ProgramClassClient.removeStudentFromProgram({
      studentId,
      offeringClientId: pendingRemoval.offeringClientId,
      summary: `Removed from ${programLabel(
        pendingRemoval,
      )} from the students page`,
    })
      .then(() => {
        setPendingRemoval(null)
        load()
      })
      .catch(() => setError(true))
      .finally(() => setIsRemoving(false))
  }

  // Unregistering from a single course lived ONLY in the kebab menu, while every
  // one of the student's classes is listed right here — you could see a class and
  // add a class, but had to leave to remove one. Same call the kebab makes: a
  // deliberate removal, which writes a Removed row that re-enrolment will never
  // revive.
  const confirmClassRemove = () => {
    if (!pendingClassRemoval) {
      return
    }
    setIsRemoving(true)
    CourseClassRegistrationClient.dummyUnregisterStudentFromClass(
      pendingClassRemoval.courseClassId,
      studentId,
      new Date(),
      actingUser?.clientId ?? "",
    )
      .then(() => {
        setPendingClassRemoval(null)
        load()
      })
      .catch(() => setError(true))
      .finally(() => setIsRemoving(false))
  }

  const filterQuery = classFilter.trim().toLowerCase()
  const matchesFilter = (courseClass: StudentCourseClass) =>
    filterQuery.length > 0 &&
    courseClass.courseClassName.toLowerCase().includes(filterQuery)

  // Class chips render grouped program → classes (most recent first within
  // each program, program order as campus sorts them) — the grouping the old
  // one-row strip could only afford inside its popover.
  const programClassGroups = (programs ?? [])
    .map((program) => ({
      program,
      classes: [...program.classes].reverse(),
    }))
    .filter((group) => group.classes.length > 0)

  // Filter verdict: every chip is inline now, so the highlight IS the verdict
  // — the only extra line left is the honest "no match".
  const totalMatches =
    programClassGroups.flatMap((group) => group.classes).filter(matchesFilter)
      .length + unattributedClasses.filter(matchesFilter).length

  // The highlight also SCROLLS to its first hit — a match deep in the list
  // must not stay invisible below the cut.
  useEffect(() => {
    if (filterQuery.length === 0) {
      return
    }
    scrollBodyRef.current
      ?.querySelector('[data-filter-match="true"]')
      ?.scrollIntoView({ block: "nearest" })
  }, [filterQuery])

  const classChip = (courseClass: StudentCourseClass) => (
    // Course-class chips are NOT links on purpose: their id is a campus
    // course_class id, and no LMS route keys on it (the program page's
    // :classId is an offering client id) — a link here would be a dead link.
    <Tooltip
      key={courseClass.courseClassId}
      title={courseClass.courseClassName}
    >
      <Chip
        size="small"
        variant="outlined"
        label={courseClass.courseClassName}
        data-filter-match={matchesFilter(courseClass) ? "true" : undefined}
        sx={{
          maxWidth: "100%",
          ...chipHighlightSx(matchesFilter(courseClass)),
        }}
      />
    </Tooltip>
  )

  // A class the student has BECAUSE they are in the programme is not removable
  // here: it belongs to the programme membership, and pulling one out
  // individually just puts their classes out of step with the programme they are
  // enrolled in. Removing the programme is what removes those.
  //
  // A class OUTSIDE a programme is different — it was added to this student on
  // purpose, one at a time, and it is the only kind you can add here. So it is
  // the only kind you can take away here.
  const unattributedClassChip = (courseClass: StudentCourseClass) => (
    <Tooltip
      key={courseClass.courseClassId}
      title={`${courseClass.courseClassName} · not part of a program`}
    >
      <Chip
        size="small"
        variant="outlined"
        label={courseClass.courseClassName}
        data-filter-match={matchesFilter(courseClass) ? "true" : undefined}
        onDelete={() => setPendingClassRemoval(courseClass)}
        sx={{
          maxWidth: "100%",
          ...chipHighlightSx(matchesFilter(courseClass)),
        }}
      />
    </Tooltip>
  )

  return (
    <Box
      sx={{
        p: "16px",
        borderBottom: showHeader ? "1px solid #eee" : "none",
        flex: "1 1 auto",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {showHeader && (
        // Locality: the affordance that adds to this section sits ON the
        // section header, not at the tile's far bottom (Mayer). It also
        // stays reachable for students with zero programs, where the
        // filter-and-add bottom row never renders.
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
            mb: "8px",
            flexShrink: 0,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Programs
          </Typography>
          {/* EVERY control of this section lives on its header (Mayer:
              controls at the tile's bottom are not locality) — the class
              filter narrows the list below, Add extends it. */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              minWidth: 0,
            }}
          >
            {programClassGroups.length + unattributedClasses.length > 0 && (
              <>
                {filterQuery.length > 0 && totalMatches === 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ minWidth: 0, textTransform: "none" }}
                  >
                    no match
                  </Typography>
                )}
                <TextField
                  size="small"
                  value={classFilter}
                  onChange={(event) => setClassFilter(event.target.value)}
                  placeholder="Filter classes"
                  inputProps={{ "aria-label": "Filter classes" }}
                  sx={{ width: 180 }}
                />
              </>
            )}
            <Button
              size="small"
              startIcon={
                <SvgIcon
                  component={appIcons.plus}
                  inheritViewBox
                  sx={{
                    width: 16,
                    height: 16,
                    fill: "none",
                    stroke: "currentColor",
                  }}
                />
              }
              onClick={() => setIsAddOpen(true)}
            >
              Add program
            </Button>
            <Button
              size="small"
              startIcon={
                <SvgIcon
                  component={appIcons.plus}
                  inheritViewBox
                  sx={{
                    width: 16,
                    height: 16,
                    fill: "none",
                    stroke: "currentColor",
                  }}
                />
              }
              onClick={openRegisterToCourse}
            >
              Register to course
            </Button>
          </Box>
        </Box>
      )}
      {programs === null && !error && (
        <Box
          sx={{
            flex: "1 1 auto",
            minHeight: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={18} />
        </Box>
      )}
      {error && (
        <Typography variant="body2" color="error">
          Couldn&apos;t load programs.{" "}
          <Button size="small" onClick={load}>
            Retry
          </Button>
        </Typography>
      )}
      {programs && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          {programs.length === 0 && (
            <Typography variant="body2" color="text.disabled">
              No active programs
            </Typography>
          )}
          {/* THE scrollable tile body: program cloud, per-program class
              groups and the outside-a-program group, all fully inline. Only
              this box scrolls — the caption above and the filter/add rows
              below stay pinned. */}
          <Box
            ref={scrollBodyRef}
            data-programs-tile-body
            sx={{
              flex: "1 1 auto",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              ...tileScrollSx,
            }}
          >
            {programs.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {programs.map((program) => {
                  const linkedProgramId = programByOffering?.get(
                    program.offeringClientId,
                  )?.id
                  if (!linkedProgramId) {
                    return (
                      <Tooltip
                        key={program.offeringClientId}
                        title={programFullLabel(program)}
                      >
                        <Chip
                          size="small"
                          variant="outlined"
                          label={programLabel(program)}
                          onDelete={() => setPendingRemoval(program)}
                          sx={{ maxWidth: "100%" }}
                        />
                      </Tooltip>
                    )
                  }
                  return (
                    // The chip body is the link (same-tab router navigation
                    // to the program page); the X keeps removing —
                    // preventDefault stops the anchor from also navigating,
                    // stopPropagation keeps the panel from toggling. The
                    // tooltip carries the full offering descriptor.
                    <Tooltip
                      key={program.offeringClientId}
                      title={programFullLabel(program)}
                    >
                      <Chip
                        component={RouterLink}
                        to={`/program/${linkedProgramId}`}
                        clickable
                        size="small"
                        variant="outlined"
                        label={programLabel(program)}
                        onClick={(event) => event.stopPropagation()}
                        onDelete={(event: ReactMouseEvent) => {
                          event.preventDefault()
                          event.stopPropagation()
                          setPendingRemoval(program)
                        }}
                        sx={{
                          maxWidth: "100%",
                          "&:hover .MuiChip-label": {
                            textDecoration: "underline",
                          },
                        }}
                      />
                    </Tooltip>
                  )
                })}
              </Box>
            )}
            {programClassGroups.map((group) => (
              <Box key={group.program.offeringClientId}>
                <Typography variant="caption" color="text.secondary">
                  {programLabel(group.program)}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "6px",
                    mt: "4px",
                  }}
                >
                  {group.classes.map(classChip)}
                </Box>
              </Box>
            ))}
            {unattributedClasses.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {unattributedClasses.length}{" "}
                  {unattributedClasses.length === 1 ? "class" : "classes"}{" "}
                  outside a program
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "6px",
                    mt: "4px",
                  }}
                >
                  {unattributedClasses.map(unattributedClassChip)}
                </Box>
              </Box>
            )}
          </Box>
          {!showHeader &&
            programClassGroups.length + unattributedClasses.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  minWidth: 0,
                  flexShrink: 0,
                }}
              >
                {/* Headerless hosts (the dialog) keep the controls row at the
                    bottom; panel hosts carry them on the section header. */}
                <TextField
                  size="small"
                  value={classFilter}
                  onChange={(event) => setClassFilter(event.target.value)}
                  placeholder="Filter classes"
                  inputProps={{ "aria-label": "Filter classes" }}
                  sx={{ flex: "1 1 180px", minWidth: 140, maxWidth: 320 }}
                />
                {filterQuery.length > 0 && totalMatches === 0 && (
                  <Tooltip title={`no match for "${classFilter.trim()}"`}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ minWidth: 0 }}
                    >
                      no match for &quot;{classFilter.trim()}&quot;
                    </Typography>
                  </Tooltip>
                )}
              </Box>
            )}
          {!showHeader && (
            <Box sx={{ flexShrink: 0 }}>
              <Button
                size="small"
                startIcon={
                  <SvgIcon
                    component={appIcons.plus}
                    inheritViewBox
                    sx={{
                      width: 16,
                      height: 16,
                      fill: "none",
                      stroke: "currentColor",
                    }}
                  />
                }
                onClick={() => setIsAddOpen(true)}
              >
                Add program
              </Button>
              <Button
                size="small"
                startIcon={
                  <SvgIcon
                    component={appIcons.plus}
                    inheritViewBox
                    sx={{
                      width: 16,
                      height: 16,
                      fill: "none",
                      stroke: "currentColor",
                    }}
                  />
                }
                onClick={openRegisterToCourse}
              >
                Register to course
              </Button>
            </Box>
          )}
        </Box>
      )}

      <AddProgramDialog
        open={isAddOpen}
        studentId={studentId}
        onClose={() => setIsAddOpen(false)}
        onAdded={load}
      />

      <GenericDialog
        open={pendingRemoval !== null}
        onClose={() => !isRemoving && setPendingRemoval(null)}
        size="xs"
        title="Remove from program?"
        content={
          <Typography variant="body2">
            This ends {studentName ? `${studentName}'s` : "the student's"}{" "}
            access to{" "}
            <b>{pendingRemoval ? programLabel(pendingRemoval) : ""}</b> and its
            course materials. Their other programs are unaffected, and you can
            re-add them anytime.
          </Typography>
        }
        buttons={[
          {
            type: "secondary",
            text: "Cancel",
            onClick: () => setPendingRemoval(null),
            disabled: isRemoving,
          },
          {
            type: "danger",
            text: "Remove",
            onClick: confirmRemove,
            loading: isRemoving,
          },
        ]}
      />
      <GenericDialog
        open={pendingClassRemoval !== null}
        onClose={() => !isRemoving && setPendingClassRemoval(null)}
        size="xs"
        title="Remove from class?"
        content={
          <Typography variant="body2">
            This takes {studentName ? `${studentName}` : "the student"} out of{" "}
            <b>{pendingClassRemoval?.courseClassName ?? ""}</b>. It is not part
            of a program, so nothing else changes — and because it is a
            deliberate removal, re-enrolling them in a program will not bring it
            back.
          </Typography>
        }
        buttons={[
          {
            type: "secondary",
            text: "Cancel",
            onClick: () => setPendingClassRemoval(null),
            disabled: isRemoving,
          },
          {
            type: "danger",
            text: "Remove",
            onClick: confirmClassRemove,
            loading: isRemoving,
          },
        ]}
      />
    </Box>
  )
}
